import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Filter, Eye, Shield, ShieldOff, RotateCcw, Upload } from 'lucide-react';
import { accountService } from '../api/accountRepo';
import { Account } from '../types';
import { getRoleDisplayName } from '../utils';
import { canManageStaffDB } from '../../../lib/rbac';
import { useStore } from '../../../lib/store';
import toast from 'react-hot-toast';

const AccountList: React.FC = () => {
  const { user } = useStore();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [canManageAccounts, setCanManageAccounts] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  const roles = [
    'doctor', 'nurse', 'pharmacist', 'lab_tech',
    'reception', 'billing', 'cleaner', 'security', 'admin', 'other'
  ];

  const statuses = ['active', 'invited', 'disabled'];

  useEffect(() => {
    checkPermissions();
  }, [user]);

  useEffect(() => {
    if (!checkingPermissions && canManageAccounts) {
      loadAccounts();
    }
  }, [searchTerm, roleFilter, statusFilter, checkingPermissions, canManageAccounts]);

  const checkPermissions = async () => {
    if (!user?.id) {
      setCanManageAccounts(false);
      setCheckingPermissions(false);
      return;
    }

    try {
      const canManage = await canManageStaffDB(user.id);
      setCanManageAccounts(canManage);
    } catch (error) {
      console.error('Error checking permissions:', error);
      setCanManageAccounts(false);
    } finally {
      setCheckingPermissions(false);
    }
  };

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const filters = {
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { status: statusFilter }),
        ...(searchTerm && { search: searchTerm }),
      };
      const data = await accountService.list(filters);
      setAccounts(data);
    } catch (error) {
      toast.error('Failed to load accounts');
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (accountId: string, newStatus: 'active' | 'disabled') => {
    try {
      await accountService.setStatus(accountId, newStatus);
      toast.success(`Account ${newStatus}`);
      loadAccounts();
    } catch (error) {
      toast.error(`Failed to ${newStatus} account`);
      console.error('Error updating status:', error);
    }
  };

  const handleResetPassword = async (accountId: string) => {
    try {
      const result = await accountService.resetPassword(accountId);
      toast.success(`Password reset. Temp password: ${result.tempPassword}`);
    } catch (error) {
      toast.error('Failed to reset password');
      console.error('Error resetting password:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      active: "bg-green-100 text-green-800",
      invited: "bg-yellow-100 text-yellow-800",
      disabled: "bg-red-100 text-red-800"
    };
    return colors[status as keyof typeof colors] || "bg-gray-100 text-gray-800";
  };

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
  };

  if (checkingPermissions) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!canManageAccounts) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500">Access denied. Admin privileges required.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Account Management</h1>
          <p className="text-gray-600">Manage staff login accounts and permissions</p>
        </div>
        <div className="flex space-x-3">
          <Link
            to="/staff/accounts/import"
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
          >
            <Upload className="w-4 h-4" />
            <span>Bulk Import</span>
          </Link>
          <Link
            to="/staff/accounts/new"
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create Account</span>
          </Link>
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Roles</option>
            {roles.map(role => (
              <option key={role} value={role}>{getRoleDisplayName(role)}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            {statuses.map(status => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>

          <button
            onClick={clearFilters}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Clear</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {accounts.map((account) => (
                <tr key={account.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{account.email}</div>
                      <div className="text-sm text-gray-500">{account.phone || 'No phone'}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{getRoleDisplayName(account.role)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(account.status)}`}>
                      {account.status.charAt(0).toUpperCase() + account.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {account.lastLoginAt ? new Date(account.lastLoginAt).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <Link
                        to={`/staff/accounts/${account.id}`}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Account"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                      {account.status === 'active' ? (
                        <button
                          onClick={() => handleStatusChange(account.id, 'disabled')}
                          className="text-red-600 hover:text-red-900"
                          title="Disable Account"
                        >
                          <ShieldOff className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleStatusChange(account.id, 'active')}
                          className="text-green-600 hover:text-green-900"
                          title="Activate Account"
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleResetPassword(account.id)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Reset Password"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {accounts.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500">No accounts found</div>
            <Link
              to="/staff/accounts/new"
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create First Account
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountList;
